import React, { useCallback, useEffect, useState } from "react";
import { REWARD_SHOP_ENABLED } from "../../constants/rewardShop";
import { FiGift, FiRefreshCw, FiSettings, FiStar, FiTrendingUp } from "react-icons/fi";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useAuth } from "../../auth/AuthContext";
import { cacheStudentAvatarDisplayUrl } from "../../constants/studentAvatarCache";
import { getStudentAvatarDisplayUrl } from "../../constants/defaultStudentAvatars";
import { useCachedStudentAvatarUrl } from "../../hooks/useCachedStudentAvatarUrl";
import StudentAvatarImage from "../fragment/StudentAvatarImage";
import {
    getGamificationClasses,
    getGamificationLeaderboard,
    getGamificationSummary
} from "../../services/gamificationService";
import { readAppShellCacheEntry, writeAppShellCache } from "../../services/appShellCache";
import { fetchStudentPageCache, readStudentPageCache, studentPageScope } from "../../services/studentPageCache";
import "./css/Gamification.scss";
import "./css/StudentPagesMinimal.scss";

const PERIODS = [
    { value: "week", label: "本週" },
    { value: "month", label: "本月" },
    { value: "all", label: "總排行" }
];

const getInitial = name => String(name || "A").trim().charAt(0).toUpperCase() || "A";
const formatNumber = value => Number(value || 0).toLocaleString("zh-TW");

function LearningLeaderboard() {
    const { firebaseUser, role, studentProfile } = useAuth();
    const isStudent = role === "student";
    const isStaff = role === "teacher" || role === "admin";
    const hasRewardsAccess = REWARD_SHOP_ENABLED && isStudent
        && studentProfile?.learner_type === "academy_student"
        && studentProfile?.membership?.effective_access?.plan_codes?.includes("academy_internal") === true;
    const [period, setPeriod] = useState("week");
    const [leaderboardScope, setLeaderboardScope] = useState(studentProfile?.class ? "class" : "overall");
    const [classCode, setClassCode] = useState(studentProfile?.class || "");
    const [classes, setClasses] = useState([]);
    const [data, setData] = useState(null);
    const [summary, setSummary] = useState(() => (
        isStudent && firebaseUser?.uid
            ? readAppShellCacheEntry(firebaseUser.uid, "gamification", {
                freshForMs: 5 * 60 * 1000,
                keepForMs: 24 * 60 * 60 * 1000
            })?.value || null
            : null
    ));
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const cachedAvatarUrl = useCachedStudentAvatarUrl(summary?.profile?.avatar_url || studentProfile?.avatar_url, {
        ownerUid: firebaseUser?.uid,
        sourceKey: studentProfile?.user_image || studentProfile?.userimage
    });

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!firebaseUser) return;
        // Students see the last ranking they loaded at once, then the fresh one replaces it.
        const cacheScope = isStudent ? studentPageScope(firebaseUser, role, studentProfile) : "";
        const cacheKey = `leaderboard:${leaderboardScope}:${period}`;
        const cached = cacheScope ? readStudentPageCache(cacheScope, cacheKey)?.value : null;
        if (cached) setData(cached);
        silent || cached ? setRefreshing(true) : setLoading(true);
        if (cached) setLoading(false);
        try {
            const fetchLeaderboard = () => getGamificationLeaderboard(firebaseUser, period, isStaff ? classCode || null : null, leaderboardScope);
            const requests = [
                cacheScope ? fetchStudentPageCache(cacheScope, cacheKey, fetchLeaderboard, { force: true }) : fetchLeaderboard()
            ];
            if (isStudent) requests.push(getGamificationSummary(firebaseUser));
            if (isStaff) requests.push(getGamificationClasses(firebaseUser));

            const results = await Promise.all(requests);
            setData(results[0]);
            if (isStudent) {
                cacheStudentAvatarDisplayUrl(results[1]?.profile?.avatar_url, {
                    ownerUid: firebaseUser.uid,
                    sourceKey: studentProfile?.user_image || studentProfile?.userimage
                });
                setSummary(results[1]);
                writeAppShellCache(firebaseUser.uid, "gamification", results[1]);
            }
            if (isStaff) {
                const classResult = results[1];
                const nextClasses = classResult?.classes || [];
                setClasses(nextClasses);
                if (!classCode && nextClasses.length > 0) setClassCode(nextClasses[0]);
            }
        } catch (error) {
            toast.error(error.message || "排行榜讀取失敗");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [firebaseUser, role, studentProfile, period, classCode, leaderboardScope, isStudent, isStaff]);

    useEffect(() => {
        load();
    }, [load]);

    const rows = data?.leaderboard || [];
    const isOverall = data?.scope === "overall" || leaderboardScope === "overall";
    const currentClass = isOverall ? "" : (data?.class_code || classCode || studentProfile?.class || "");

    return (
        <main className={`gamification-page${isStudent ? " gamification-page--student" : ""}`}>
            <section className="gamification-hero">
                <div>
                    <span className="gamification-eyebrow"><FiTrendingUp /> {isOverall ? "OVERALL LEADERBOARD" : "CLASS LEADERBOARD"}</span>
                    <h1>{isOverall ? "綜合排行榜" : (currentClass ? `${currentClass} 班排行榜` : "我的班級排行榜")}</h1>
                    <p>{REWARD_SHOP_ENABLED ? "完成聽力、作業與遊戲都能累積 XP；排行榜以 XP 排名，兌換獎品不會讓排名下降。" : "完成聽力、作業與口說都能累積 XP；排行榜以 XP 排名。"}</p>
                </div>
                <div className="gamification-hero__actions">
                    <div className="gamification-segment" role="group" aria-label="排行榜範圍">
                        <button
                            className={leaderboardScope === "class" ? "active" : ""}
                            type="button"
                            aria-pressed={leaderboardScope === "class"}
                            onClick={() => setLeaderboardScope("class")}
                            disabled={isStudent && !studentProfile?.class}
                        >
                            {isStudent ? "我的班級" : "班級排行"}
                        </button>
                        <button
                            className={leaderboardScope === "overall" ? "active" : ""}
                            type="button"
                            aria-pressed={leaderboardScope === "overall"}
                            onClick={() => setLeaderboardScope("overall")}
                        >
                            綜合排行
                        </button>
                    </div>
                    {isStaff && leaderboardScope === "class" && classes.length > 0 && (
                        <label className="gamification-select">
                            <span>班級</span>
                            <select value={classCode} onChange={event => setClassCode(event.target.value)}>
                                {classes.map(item => <option key={item} value={item}>{item}</option>)}
                            </select>
                        </label>
                    )}
                    <div className="gamification-segment" role="group" aria-label="排行榜期間">
                        {PERIODS.map(item => (
                            <button
                                className={period === item.value ? "active" : ""}
                                key={item.value}
                                type="button"
                                aria-pressed={period === item.value}
                                onClick={() => setPeriod(item.value)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <button className="gamification-refresh" type="button" onClick={() => load({ silent: true })} disabled={refreshing}>
                        <FiRefreshCw className={refreshing ? "is-spinning" : ""} />
                        更新
                    </button>
                </div>
            </section>

            {isStudent && summary && (
                <section className="gamification-me-card">
                    <div className="gamification-avatar-wrap">
                        {cachedAvatarUrl
                            ? <StudentAvatarImage className="gamification-avatar gamification-avatar--large" src={getStudentAvatarDisplayUrl(cachedAvatarUrl, 240)} alt={`${summary.profile?.nickname || summary.profile?.name || "學生"} 的排行榜照片`} />
                            : <div className="gamification-avatar gamification-avatar--large gamification-avatar--fallback">{getInitial(summary.profile?.nickname || summary.profile?.name)}</div>}
                    </div>
                    <div className="gamification-me-card__identity">
                        <span>我的學習角色</span>
                        <strong>{summary.profile?.nickname || summary.profile?.name || studentProfile?.nickname || studentProfile?.name}</strong>
                        <Link to="/student/settings"><FiSettings />到設定更換頭像</Link>
                    </div>
                    <div className="gamification-level-badge">
                        <span>目前等級</span>
                        <strong>Lv.{summary.balance?.level || 1}</strong>
                    </div>
                    <div className="gamification-me-card__stats">
                        <div className="gamification-stat">
                            <span>TOTAL XP</span>
                            <strong>{formatNumber(summary.balance?.total_xp)} XP</strong>
                        </div>
                        {REWARD_SHOP_ENABLED && <div className="gamification-stat">
                            <span>AE POINTS</span>
                            <strong>{formatNumber(summary.balance?.points_balance)} P</strong>
                        </div>}
                    </div>
                    {hasRewardsAccess && <Link className="gamification-reward-link" to="/student/rewards"><FiGift />獎品商城</Link>}
                </section>
            )}

            <section className="gamification-ranking-card">
                <header>
                    <div>
                        <span>RANKING</span>
                        <h2>{period === "week" ? "本週 XP 排行" : period === "month" ? "本月 XP 排行" : "累積 XP 排行"}</h2>
                    </div>
                    {REWARD_SHOP_ENABLED && <div className="gamification-scoring-note"><FiStar />XP 不會因為兌換獎品而扣除</div>}
                </header>

                {loading ? (
                    <div className="gamification-loading">排行榜載入中…</div>
                ) : rows.length === 0 ? (
                    <div className="gamification-empty">{isOverall ? "目前還沒有綜合 XP 紀錄。" : "這個班級還沒有 XP 紀錄。"}</div>
                ) : (
                    <div className="gamification-ranking-list">
                        {rows.map(row => {
                            const rank = Number(row.rank_position || 0);
                            const medal = rank === 1 ? "🥇" : rank === 2 ? "🥈" : rank === 3 ? "🥉" : rank;
                            return (
                                <article className={`gamification-ranking-row ${row.is_current_user ? "is-me" : ""} ${rank <= 3 ? `is-top is-top-${rank}` : ""}`} key={row.student_id}>
                                    <div className="gamification-rank">{medal}</div>
                                    <div className="gamification-row-avatar">
                                        {row.avatar_url
                                            ? <StudentAvatarImage className="gamification-avatar" src={getStudentAvatarDisplayUrl(row.avatar_url, 128)} alt="" />
                                            : <div className="gamification-avatar gamification-avatar--fallback">{getInitial(row.nickname || row.student_name)}</div>}
                                    </div>
                                    <div className="gamification-student-copy">
                                        <strong>{row.nickname || row.student_name}{row.is_current_user ? " · 你" : ""}</strong>
                                        {row.nickname && row.student_name && row.nickname !== row.student_name && (
                                            <span className="gamification-student-original">原名：{row.student_name}</span>
                                        )}
                                        <span>{row.class_name ? `${row.class_name} 班` : "Alan English"} · Lv.{row.level || 1}</span>
                                    </div>
                                    <div className="gamification-xp">
                                        <strong>{formatNumber(row.period_xp)} XP</strong>
                                        <span>累積 {formatNumber(row.total_xp)} XP</span>
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                )}
            </section>
        </main>
    );
}

export default LearningLeaderboard;
